import React, { useEffect, useState } from 'react'
import apiClient from '../../services/api'
import { useToast } from '../../context/ToastContext'
import LoadingSpinner from '../../components/LoadingSpinner'

const serviceFilters = [
  { key: 'all', label: 'All Services' },
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' }
]

const AdminServices = () => {
  const [services, setServices] = useState([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState({ title: '', description: '', price: '', isActive: true })
  const [editId, setEditId] = useState(null)
  const [statusFilter, setStatusFilter] = useState('all')
  const { addToast } = useToast()
  const loadServices = React.useCallback(async () => {
    try {
      const response = await apiClient.get('/api/services')
      setServices(response.data)
    } catch (error) {
      console.error('Error fetching services', error)
      addToast('Error fetching services', 'danger')
    } finally {
      setLoading(false)
    }
  }, [addToast])

  useEffect(() => {
    loadServices()
  }, [loadServices])

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const submitService = async (event) => {
    event.preventDefault()

    try {
      if (editId) {
        await apiClient.put(`/api/services/${editId}`, {
          ...form,
          price: Number(form.price)
        })
      } else {
        await apiClient.post('/api/services', {
          ...form,
          price: Number(form.price)
        })
      }
      setForm({ title: '', description: '', price: '', isActive: true })
      setEditId(null)
      loadServices()
      addToast(editId ? 'Service updated' : 'Service added', 'success')
    } catch (error) {
      console.error('Error saving service', error)
      addToast('Error saving service', 'danger')
    }
  }

  const editService = (service) => {
    setForm({
      title: service.title,
      description: service.description,
      price: service.price,
      isActive: service.isActive
    })
    setEditId(service._id)
  }

  const deleteService = async (id) => {
    if (!window.confirm('Delete this service?')) return
    try {
      await apiClient.delete(`/api/services/${id}`)
      setServices((prev) => prev.filter((s) => s._id !== id))
    } catch (error) {
      console.error('Error deleting service', error)
    }
  }

  if (loading) return <LoadingSpinner />

  const filteredServices = services.filter((service) => {
    if (statusFilter === 'active') return service.isActive
    if (statusFilter === 'inactive') return !service.isActive
    return true
  })

  const filterCount = (key) => {
    if (key === 'active') return services.filter((service) => service.isActive).length
    if (key === 'inactive') return services.filter((service) => !service.isActive).length
    return services.length
  }

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h2>Services</h2>
          <p>
            This page now separates editing from browsing more clearly, so it is easier to manage pricing and visibility without scanning the whole table each time.
          </p>
        </div>
      </div>

      <div className="card p-3 mb-4 shadow-sm admin-form-card">
        <h5>{editId ? 'Edit Service' : 'Add Service'}</h5>
        <form onSubmit={submitService} className="row g-3">
          <div className="col-md-4">
            <input name="title" value={form.title} onChange={handleChange} placeholder="e.g., Cardiology, Dental Care" className="form-control" required />
          </div>
          <div className="col-md-4">
            <input name="description" value={form.description} onChange={handleChange} placeholder="e.g., Heart health exam, ECG, and diagnostic support" className="form-control" required />
          </div>
          <div className="col-md-2">
            <input name="price" type="number" value={form.price} onChange={handleChange} placeholder="e.g., 99" className="form-control" required />
          </div>
          <div className="col-md-2 d-flex align-items-center">
            <div className="form-check">
              <input id="activeCheck" name="isActive" type="checkbox" className="form-check-input" checked={form.isActive} onChange={handleChange} />
              <label className="form-check-label" htmlFor="activeCheck">Active</label>
            </div>
          </div>
          <div className="col-12">
            <button className="btn btn-primary" type="submit">{editId ? 'Update' : 'Add'} Service</button>
          </div>
        </form>
      </div>

      <div className="admin-toolbar">
        <div className="admin-filter-chips">
          {serviceFilters.map((filter) => (
            <button
              key={filter.key}
              type="button"
              className={`admin-filter-chip${statusFilter === filter.key ? ' is-active' : ''}`}
              onClick={() => setStatusFilter(filter.key)}
            >
              {filter.label}
              <span className="admin-filter-chip-count">{filterCount(filter.key)}</span>
            </button>
          ))}
        </div>
        <div className="admin-note">{filteredServices.length} service{filteredServices.length === 1 ? '' : 's'} shown</div>
      </div>

      <div className="table-responsive">
        <table className="table table-striped shadow-sm">
          <thead>
            <tr>
              <th>Title</th>
              <th>Description</th>
              <th>Price</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredServices.map((service) => (
              <tr key={service._id}>
                <td>
                  <div className="admin-table-cell-stack">
                    <span className="admin-table-label">{service.title}</span>
                  </div>
                </td>
                <td className="admin-table-meta">{service.description}</td>
                <td>${service.price}</td>
                <td>
                  <span className={`admin-status-badge ${service.isActive ? 'admin-status-active' : 'admin-status-inactive'}`}>
                    {service.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div className="admin-action-group">
                    <button className="btn btn-sm btn-secondary" onClick={() => editService(service)}>Edit</button>
                    <button className="btn btn-sm btn-danger" onClick={() => deleteService(service._id)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default AdminServices
